"use client";
import { useEffect, useState } from "react";
import CustomButton, {
  BtnRadius,
  BtnSizes,
  BtnTypes,
} from "./components/lib/CustomButton";
import OfflinePage from "./components/Errors/offline/offline";
import CookiesBlockedPage from "./components/Errors/CookiesBlockedPage";

interface ErrorPageProps {
  error: Error & { digest?: string };
  reset: () => void;
}

const ErrorPage: React.FC<ErrorPageProps> = ({ error, reset }) => {
  const [errorInfo, setErrorInfo] = useState<string>("");
  const [logError, setLogError] = useState<string>("");

  useEffect(() => {
    const getErrorInfo = () => {
      const isServerSide = !!error.digest;
      if (isServerSide) {
        return `Server Error: ${error.message}\nDigest: ${error.digest}`;
      }
      return `Client Error: ${error.message}\nStack: ${error.stack}`;
    };

    const errorInfoString = getErrorInfo();
    setErrorInfo(errorInfoString);

    // Log the error to Graylog via UDP
    const logError = async () => {
      try {
        const response = await fetch("/api/log-error", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            host: window.location.hostname,
            message: error.message,
            stack: error.stack,
            name: error.name,
            digest: error.digest,
            url: window.location.href,
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to log error");
        }
      } catch (err) {
        setLogError("Failed to send error log to Graylog");
        // console.error("Logging error:", err);
      }
    };

    logError();

    // Also log to console for local debugging
    // console.error("Error occurred:", error.name);
  }, [error]);
  if (["ChunkLoadError"].includes(error.name)) return <OfflinePage />;
  if (["SecurityError"].includes(error.name)) return <CookiesBlockedPage />;

  return (
    <div className={"errorContainer"}>
      <div className={"errorContent"}>
        <h2>Oops! Something Went Wrong</h2>
        <p className={"errorMessage"}>
          We apologize for the inconvenience. Our team has been notified and is
          working on a fix.
        </p>
        <CustomButton
          type={BtnTypes.primary}
          size={BtnSizes.small}
          radius={BtnRadius.none}
          onClick={reset}
        >
          Try Again
        </CustomButton>
        <div className={"errorDetails"}>
          <h3>Error Details:</h3>
          <pre>{errorInfo}</pre>
          {logError && <p className={"logError"}>{logError}</p>}
        </div>
      </div>
    </div>
  );
};

export default ErrorPage;
